import React from 'react';

export default function PrivacyPolicy() {
  return (
    <div style={{ maxWidth: 800, margin: '0 auto', padding: '2rem 0 4rem' }}>

      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ fontFamily: 'var(--font-head)', fontSize: '2rem', fontWeight: 600, color: 'var(--text)', letterSpacing: '-.02em', marginBottom: '.5rem' }}>Privacy Policy</h1>
        <p style={{ fontSize: '.82rem', color: 'var(--text3)', fontWeight: 300 }}>Last updated: 06 April 2026</p>
      </div>

      <Section title="1. Who we are">
        Levare Wellbeing ("Levare", "we", "us") operates the Levare Health Intelligence Platform accessible at app.levarewellbeing.com. We are the data controller for personal data collected through this platform.
        <br /><br />
        Contact: contact@levarewellbeing.com
      </Section>

      <Section title="2. What data we collect">
        We collect and process the following categories of personal data:
        <br /><br />
        <strong style={{ color: 'var(--text)' }}>Identity and contact data:</strong> Your name, email address, date of birth, gender, and ethnicity (optional).
        <br /><br />
        <strong style={{ color: 'var(--text)' }}>Health data (special category):</strong> Blood test results (glucose, cholesterol, blood pressure, vitamin levels and other biomarkers), body measurements (height, weight, BMI), mental health scores (stress, burnout, PHQ-9, GAD-7), fitness data (steps, heart rate, activity levels), medical conditions, medications, and family medical history.
        <br /><br />
        <strong style={{ color: 'var(--text)' }}>Training data:</strong> Exercise sessions, workout logs, personal bests, and training programme data.
        <br /><br />
        <strong style={{ color: 'var(--text)' }}>Nutrition data:</strong> Food diary entries, meal plans, and dietary preferences.
        <br /><br />
        <strong style={{ color: 'var(--text)' }}>Usage data:</strong> How you interact with the platform, which features you use, and when.
      </Section>

      <Section title="3. Why we collect your data and our legal basis">
        We process your personal data for the following purposes:
        <br /><br />
        <strong style={{ color: 'var(--text)' }}>To provide the service (contract):</strong> Processing your health data is necessary to deliver personalised health insights, AI-powered advice from Rico, training programmes, and meal plans.
        <br /><br />
        <strong style={{ color: 'var(--text)' }}>Explicit consent (special category health data):</strong> Under UK GDPR Article 9(2)(a), we rely on your explicit consent to process special category health data. You provide this consent when you create an account and upload your health information. You may withdraw consent at any time by deleting your account.
        <br /><br />
        <strong style={{ color: 'var(--text)' }}>Legitimate interests:</strong> To improve the platform, detect technical issues, and ensure security.
        <br /><br />
        <strong style={{ color: 'var(--text)' }}>Employer wellness programmes:</strong> If your employer has onboarded to Levare, your HR manager can access anonymised, aggregated team health data only. Your individual results are never shared with your employer.
      </Section>

      <Section title="4. How we store your data">
        Your data is stored securely using Google Firebase Firestore, hosted in the European Union. Data is encrypted at rest and in transit.
        <br /><br />
        Access to your individual health data is restricted to you personally. Levare's clinical team may access individual data only where necessary to provide clinical support, and only with your knowledge.
        <br /><br />
        AI processing: When you interact with Rico (our AI advisor), your health data is sent to Anthropic's API to generate personalised responses. Anthropic processes this data in accordance with their privacy policy. We do not share your name or contact details with Anthropic — only anonymised health metrics.
      </Section>

      <Section title="5. How long we keep your data">
        We retain your personal data for as long as your account is active. If you delete your account, your personal data will be deleted within 30 days, except where we are required to retain it for legal or regulatory purposes.
        <br /><br />
        Aggregated, anonymised data (with no individual identifiers) may be retained for platform analytics and improvement purposes.
      </Section>

      <Section title="6. Who we share your data with">
        We do not sell your personal data. We share data only with the following third parties where necessary to operate the service:
        <br /><br />
        <strong style={{ color: 'var(--text)' }}>Google Firebase</strong> — database and authentication infrastructure<br />
        <strong style={{ color: 'var(--text)' }}>Anthropic</strong> — AI processing for Rico health, training and nutrition advice<br />
        <strong style={{ color: 'var(--text)' }}>Vercel</strong> — platform hosting<br />
        <strong style={{ color: 'var(--text)' }}>Render</strong> — API server hosting
        <br /><br />
        If your employer participates in a Levare wellness programme, your HR manager can view anonymised, aggregated team health statistics only. Individual data is never shared with employers.
      </Section>

      <Section title="7. Your rights under UK GDPR">
        You have the following rights regarding your personal data:
        <br /><br />
        <strong style={{ color: 'var(--text)' }}>Right of access:</strong> You can request a copy of all personal data we hold about you.<br /><br />
        <strong style={{ color: 'var(--text)' }}>Right to rectification:</strong> You can correct inaccurate data at any time via My Profile and Upload Data.<br /><br />
        <strong style={{ color: 'var(--text)' }}>Right to erasure:</strong> You can request deletion of your account and all associated data.<br /><br />
        <strong style={{ color: 'var(--text)' }}>Right to data portability:</strong> You can request your data in a structured, machine-readable format.<br /><br />
        <strong style={{ color: 'var(--text)' }}>Right to withdraw consent:</strong> You can withdraw consent for processing special category health data at any time by deleting your account.<br /><br />
        <strong style={{ color: 'var(--text)' }}>Right to object:</strong> You can object to processing based on legitimate interests.<br /><br />
        To exercise any of these rights, contact us at contact@levarewellbeing.com. We will respond within 30 days.
        <br /><br />
        You also have the right to lodge a complaint with the Information Commissioner's Office (ICO) at ico.org.uk.
      </Section>

      <Section title="8. Health data and medical disclaimer">
        The Levare platform and Rico AI advisor provide general health information and wellness support only. Nothing on this platform constitutes medical advice, diagnosis, or treatment.
        <br /><br />
        Always consult a qualified healthcare professional before making changes to your diet, exercise routine, or medication. If you have a medical emergency, call 999 immediately.
        <br /><br />
        We process your health data with the understanding that you are sharing it voluntarily to receive personalised wellness guidance.
      </Section>

      <Section title="9. Cookies and tracking">
        We use only essential cookies required for authentication and platform functionality. We do not use advertising cookies or sell data to advertisers. The platform does not display advertisements.
      </Section>

      <Section title="10. Changes to this policy">
        We may update this privacy policy from time to time. We will notify you of material changes via email or a notice on the platform. The date at the top of this page shows when the policy was last updated.
      </Section>

      <Section title="11. Contact us">
        For any privacy-related questions or to exercise your rights:
        <br /><br />
        Levare Wellbeing<br />
        Email: contact@levarewellbeing.com<br />
        Website: www.levarewellbeing.com
      </Section>

    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: '2rem' }}>
      <h2 style={{ fontFamily: 'var(--font-head)', fontSize: '1.1rem', fontWeight: 500, color: 'var(--text)', marginBottom: '.75rem', letterSpacing: '-.01em' }}>{title}</h2>
      <p style={{ fontSize: '.82rem', color: 'var(--text2)', fontWeight: 300, lineHeight: 1.8 }}>{children}</p>
    </div>
  );
}
